import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";

const PAGE_SIZE = 50;

export function usePagedList(path) {
  const [state, setState] = useState({ items: [], total: 0, page: 1, loading: true });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [debounced, setDebounced] = useState("");

  useEffect(() => { const t = setTimeout(() => { setDebounced(q); setPage(1); }, 300); return () => clearTimeout(t); }, [q]);

  const load = useCallback(async () => {
    setState(s => ({ ...s, loading: true }));
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (debounced.trim()) params.q = debounced.trim();
      if (status !== "all") params.status = status;
      const { data } = await api.get(path, { params });
      setState({ items: data.items, total: data.total, page: data.page, loading: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load");
      setState(s => ({ ...s, loading: false }));
    }
  }, [path, page, debounced, status]);

  useEffect(() => { load(); }, [load]);
  const changeStatus = (v) => { setStatus(v); setPage(1); };
  return { ...state, q, setQ, status, setStatus: changeStatus, setPage, reload: load, pageSize: PAGE_SIZE };
}

export function ListToolbar({ list, statuses, placeholder, testid }) {
  return (
    <div className="flex flex-wrap gap-3 items-center">
      <div className="relative flex-1 min-w-[220px]">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={list.q} onChange={e => list.setQ(e.target.value)} placeholder={placeholder} className="pl-9 rounded-full bg-white" data-testid={`${testid}-search`} />
      </div>
      <Select value={list.status} onValueChange={list.setStatus}>
        <SelectTrigger className="w-44 rounded-full bg-white" data-testid={`${testid}-status`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All statuses</SelectItem>
          {statuses.map(s => <SelectItem key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export function Pager({ list, testid }) {
  const pages = Math.max(1, Math.ceil(list.total / list.pageSize));
  if (list.total <= list.pageSize) return null;
  return (
    <div className="flex items-center justify-between text-sm text-slate-600" data-testid={`${testid}-pager`}>
      <span data-testid={`${testid}-page-info`}>Page {list.page} of {pages} · {list.total} total</span>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="rounded-full" disabled={list.page <= 1} onClick={() => list.setPage(list.page - 1)} data-testid={`${testid}-prev`}>
          <ChevronLeft className="w-4 h-4" /> Previous
        </Button>
        <Button size="sm" variant="outline" className="rounded-full" disabled={list.page >= pages} onClick={() => list.setPage(list.page + 1)} data-testid={`${testid}-next`}>
          Next <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
